import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { Bell, CheckCheck, Settings, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { DataTablePagination } from '@/components/shared/DataTablePagination'
import { useUrlListParams } from '@/hooks/useUrlListParams'
import {
  useNotificationsList,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useDeleteNotification,
} from '@/hooks/useNotifications'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import { formatDate } from '@/lib/format'
import {
  notificationDescription,
  notificationTitle,
} from '@/lib/notificationDisplay'
import type { AppNotification, NotificationListQuery } from '@/types'

const ALL = '__all__'

type Filters = { unread?: 'true' | 'false' }

function stringValue(data: Record<string, unknown> | null, key: string) {
  const value = data?.[key]
  return typeof value === 'string' && value.trim() ? value : null
}

function notificationDestination(n: AppNotification): string | null {
  const data = n.data
  const detailUrl = stringValue(data, 'detail_url')

  if (detailUrl) {
    try {
      const parsed = new URL(detailUrl)
      return `${parsed.pathname}${parsed.search}`
    } catch {
      if (detailUrl.startsWith('/')) return detailUrl
    }
  }

  const jobId = stringValue(data, 'job_id')
  if (jobId) return `/calendar?job=${jobId}`

  const quoteId = stringValue(data, 'quote_id')
  if (quoteId) return `/quotes/${quoteId}`

  const contractId = stringValue(data, 'contract_id')
  if (contractId) return `/contracts/${contractId}`

  const invoiceId = stringValue(data, 'invoice_id')
  if (invoiceId) return `/invoices/${invoiceId}`

  const paymentId = stringValue(data, 'payment_id')
  if (paymentId) return `/payments/${paymentId}`

  const taskId = stringValue(data, 'task_id')
  if (taskId) return `/tasks/${taskId}`

  return null
}

// 2.12 — Notification center. Paginated list (shared control), read filter,
// mark-read per item + mark-all-read.
export default function NotificationsPage() {
  const { i18n, t } = useTranslation('notifications')
  const navigate = useNavigate()
  const { page, setPage, limit, setLimit, filters, setFilters, query } =
    useUrlListParams<Filters>()
  const { data, isLoading, isError } = useNotificationsList(
    query as NotificationListQuery
  )
  const markRead = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()
  const deleteNotification = useDeleteNotification()

  const rows = useMemo(() => data?.data ?? [], [data])

  function onMarkRead(n: AppNotification) {
    if (n.read_at) return
    markRead.mutate(n.id, {
      onError: e =>
        toast({ variant: 'destructive', title: getApiErrorMessage(e) }),
    })
  }

  function onOpen(n: AppNotification) {
    const destination = notificationDestination(n)
    if (!n.read_at) {
      markRead.mutate(n.id, {
        onError: e =>
          toast({ variant: 'destructive', title: getApiErrorMessage(e) }),
      })
    }
    if (destination) navigate(destination)
  }

  function onDelete(n: AppNotification) {
    deleteNotification.mutate(n.id, {
      onSuccess: () => toast({ variant: 'success', title: t('list.deleted') }),
      onError: e =>
        toast({ variant: 'destructive', title: getApiErrorMessage(e) }),
    })
  }

  function onMarkAll() {
    markAll.mutate(undefined, {
      onSuccess: () =>
        toast({ variant: 'success', title: t('list.markedAllRead') }),
      onError: e =>
        toast({ variant: 'destructive', title: getApiErrorMessage(e) }),
    })
  }

  return (
    <div className='space-y-6'>
      <header className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h1 className='text-2xl font-bold tracking-tight'>
            {t('list.title')}
          </h1>
          <p className='text-sm text-muted-foreground'>{t('list.subtitle')}</p>
        </div>
        <div className='flex items-center gap-2'>
          <Button
            variant='outline'
            onClick={onMarkAll}
            disabled={markAll.isPending}
          >
            <CheckCheck className='mr-2 h-4 w-4' /> {t('list.markAllRead')}
          </Button>
          <Button variant='ghost' asChild>
            <Link to='/settings/notification-events'>
              <Settings className='mr-2 h-4 w-4' /> {t('list.preferences')}
            </Link>
          </Button>
        </div>
      </header>

      <div className='flex items-center gap-2'>
        <Select
          value={filters.unread ?? ALL}
          onValueChange={v =>
            setFilters({
              unread: v === ALL ? undefined : (v as 'true' | 'false'),
            })
          }
        >
          <SelectTrigger className='w-40'>
            <SelectValue placeholder={t('list.filter.placeholder')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('list.filter.all')}</SelectItem>
            <SelectItem value='true'>{t('list.filter.unread')}</SelectItem>
            <SelectItem value='false'>{t('list.filter.read')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className='p-0'>
          {isError ? (
            <p className='py-12 text-center text-sm text-destructive'>
              {t('list.loadError')}
            </p>
          ) : isLoading ? (
            <div className='space-y-2 p-4'>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className='h-14 w-full' />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className='flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground'>
              <Bell className='h-8 w-8' />
              <p className='text-sm'>{t('list.empty')}</p>
            </div>
          ) : (
            <ul className='divide-y divide-border'>
              {rows.map(n => (
                <li
                  key={n.id}
                  className={cn(
                    'flex items-start gap-3 px-4 py-3 transition-colors hover:bg-[#fbfbfc]',
                    !n.read_at && 'bg-accent/5'
                  )}
                >
                  <span
                    className={cn(
                      'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                      n.read_at ? 'bg-transparent' : 'bg-primary'
                    )}
                    aria-hidden
                  />
                  <button
                    type='button'
                    className='min-w-0 flex-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                    onClick={() => onOpen(n)}
                  >
                    <p
                      className={cn(
                        'truncate text-sm',
                        !n.read_at ? 'font-semibold' : 'font-medium'
                      )}
                    >
                      {notificationTitle(n, t, i18n.language)}
                    </p>
                    {notificationDescription(n, t, i18n.language) && (
                      <p className='text-sm text-muted-foreground'>
                        {notificationDescription(n, t, i18n.language)}
                      </p>
                    )}
                    <p className='mt-1 text-xs text-muted-foreground'>
                      {formatDate(n.created_at)}
                    </p>
                  </button>
                  {!n.read_at && (
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => onMarkRead(n)}
                      disabled={markRead.isPending}
                    >
                      {t('list.markRead')}
                    </Button>
                  )}
                  <Button
                    variant='ghost'
                    size='icon'
                    className='size-9 text-muted-foreground hover:text-destructive'
                    onClick={() => onDelete(n)}
                    disabled={
                      deleteNotification.isPending &&
                      deleteNotification.variables === n.id
                    }
                    aria-label={t('list.delete')}
                  >
                    <Trash2 className='size-4' />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {data && (
        <DataTablePagination
          metadata={data.metadata}
          page={page}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
        />
      )}
    </div>
  )
}
